import presentationChartIcon from '../assets/images/solutionsnav/PresentationChart.svg';
import shieldCheckIcon from '../assets/images/solutionsnav/ShieldCheck (1).svg';
import rocketLaunchIcon from '../assets/images/solutionsnav/RocketLaunch.svg';
import currencyCircleDollarIcon from '../assets/images/solutionsnav/CurrencyCircleDollar.svg';
import megaphoneIcon from '../assets/images/solutionsnav/Megaphone.svg';
import cpuIcon from '../assets/images/solutionsnav/Cpu.svg';

export const solutions = [
  {
    slug: 'finance-consulting',
    category: 'Finance Consulting',
    title: 'Corporate Finance Advisory',
    cardDescription:
      'Strategic guidance on capital structure, financial planning, and operational efficiency to drive sustainable enterprise value.',
    heroDescription:
      'VBS delivers finance consulting that goes beyond traditional advisory. We assess your financial ecosystem — from budgeting and cash flow to reporting — and implement strategies that strengthen performance and long-term resilience.',
    cta: 'Explore',
    icon: presentationChartIcon,
    viewTitle: 'A Holistic View of Your Financial Ecosystem.',
    viewDescription:
      'Our consultants conduct in-depth assessments of budget structures, cash flow dynamics, and financial reporting processes to uncover optimization opportunities. We work closely with your team to implement robust strategies that improve workflows and support sustainable growth.',
    viewItems: [
      'Budgeting & planning assessment',
      'Cash flow optimization',
      'Financial reporting improvement',
      'Strategy implementation support',
    ],
    deliverables: [
      {
        title: 'Budget & Planning Optimization',
        text: 'Improving budget structures to support better decision-making and control.',
      },
      {
        title: 'Cash Flow & Liquidity Visibility',
        text: 'Clarifying cash flow dynamics to uncover hidden constraints and opportunities.',
      },
      {
        title: 'Financial Reporting & Process Improvement',
        text: 'Strengthening reporting quality, cadence, and internal workflows.',
      },
      {
        title: 'Integrated Risk Management',
        text: 'Aligning financial decisions with market conditions and long-term foresight.',
      },
    ],
  },
  {
    slug: 'risk-resilience',
    category: 'Risk & Resilience',
    title: 'Risk & Strategic Resilience',
    cardDescription:
      'Comprehensive frameworks to identify, quantify, and mitigate systemic risks while building long-term institutional stability.',
    heroDescription:
      'VBS helps organizations identify, quantify, and mitigate risk across operations and markets. We design resilience frameworks that protect capital, strengthen governance, and support confident decision-making through uncertainty.',
    cta: 'Explore Advisory',
    icon: shieldCheckIcon,
    viewTitle: 'A Holistic View of Your Risk Landscape.',
    viewDescription:
      'Our consultants assess operational, market, and strategic exposures to uncover vulnerabilities before they become disruptions. We work closely with your team to implement resilience frameworks that protect capital and support long-term institutional stability.',
    viewItems: [
      'Risk identification & mapping',
      'Governance & controls review',
      'Resilience framework design',
      'Ongoing monitoring support',
    ],
    deliverables: [
      {
        title: 'Risk Identification & Mapping',
        text: 'Surfacing operational, market, and strategic exposures before they become disruptions.',
      },
      {
        title: 'Governance & Controls',
        text: 'Strengthening oversight structures that keep risk decisions accountable and consistent.',
      },
      {
        title: 'Resilience Framework Design',
        text: 'Building processes that protect capital and maintain stability through uncertainty.',
      },
      {
        title: 'Ongoing Risk Monitoring',
        text: 'Establishing reporting cadence so emerging risks are visible and actionable.',
      },
    ],
  },
  {
    slug: 'business-strategy',
    category: 'Business Strategy',
    title: 'Strategic Growth & Execution',
    cardDescription:
      'Data-backed strategies designed to help organizations navigate market shifts and capture new growth opportunities.',
    heroDescription:
      'VBS partners with leadership teams to define growth priorities and translate them into executable plans. We align capital, operations, and market positioning so strategy becomes measurable progress — not a static document.',
    cta: 'See Strategies',
    icon: rocketLaunchIcon,
    viewTitle: 'A Holistic View of Your Growth Strategy.',
    viewDescription:
      'Our consultants evaluate market position, operating model, and execution capacity to uncover where growth can be accelerated. We work closely with your team to implement strategies that convert priorities into measurable progress.',
    viewItems: [
      'Growth priority mapping',
      'Market opportunity analysis',
      'Execution roadmap design',
      'Leadership alignment support',
    ],
    deliverables: [
      {
        title: 'Growth Priority Mapping',
        text: 'Clarifying where capital and leadership attention will create the most value.',
      },
      {
        title: 'Market Opportunity Analysis',
        text: 'Identifying shifts and openings that can be converted into executable plans.',
      },
      {
        title: 'Execution Roadmap Design',
        text: 'Turning strategy into sequenced actions with clear ownership and milestones.',
      },
      {
        title: 'Leadership Alignment',
        text: 'Keeping decision-makers coordinated so growth stays measurable, not theoretical.',
      },
    ],
  },
  {
    slug: 'liquidity-consulting',
    category: 'Liquidity Consulting',
    title: 'Capital & Liquidity Management',
    cardDescription:
      'Sophisticated liquidity solutions ensuring optimal cash flow management and capital preservation for complex operations.',
    heroDescription:
      'VBS structures capital and liquidity programs that keep cash working efficiently. We help clients preserve flexibility, manage funding risk, and maintain the reserves required to operate through complex market cycles.',
    cta: 'Learn More',
    icon: currencyCircleDollarIcon,
    viewTitle: 'A Holistic View of Your Liquidity Position.',
    viewDescription:
      'Our consultants assess cash flow, funding structures, and reserve requirements to uncover where liquidity can work more efficiently. We work closely with your team to implement programs that preserve flexibility through market cycles.',
    viewItems: [
      'Cash flow diagnostics',
      'Capital structure review',
      'Liquidity buffer planning',
      'Funding risk management',
    ],
    deliverables: [
      {
        title: 'Cash Flow Diagnostics',
        text: 'Clarifying inflows, outflows, and timing so liquidity is never a surprise.',
      },
      {
        title: 'Capital Structure Review',
        text: 'Aligning funding mix with operating needs and long-term flexibility.',
      },
      {
        title: 'Liquidity Buffer Planning',
        text: 'Sizing reserves that protect operations through market and cycle stress.',
      },
      {
        title: 'Funding Risk Management',
        text: 'Reducing concentration and timing risk across credit and capital sources.',
      },
    ],
  },
  {
    slug: 'financial-marketing',
    category: 'Financial Marketing',
    title: 'Institutional Brand Strategy',
    cardDescription:
      'High-precision marketing and positioning strategies for financial entities looking to build trust and market authority.',
    heroDescription:
      'VBS builds institutional brand and marketing strategies that earn trust in regulated markets. We refine positioning, messaging, and market presence so your firm communicates with clarity, credibility, and authority.',
    cta: 'Learn More',
    icon: megaphoneIcon,
    viewTitle: 'A Holistic View of Your Institutional Brand.',
    viewDescription:
      'Our consultants assess positioning, messaging, and market presence to uncover how your firm can communicate with greater authority. We work closely with your team to implement brand strategies that build trust in regulated markets.',
    viewItems: [
      'Brand positioning review',
      'Market messaging refinement',
      'Audience & channel mapping',
      'Trust & authority building',
    ],
    deliverables: [
      {
        title: 'Brand Positioning Review',
        text: 'Defining how the firm should be understood in a crowded, regulated market.',
      },
      {
        title: 'Messaging Refinement',
        text: 'Sharpening language so credibility and differentiation come through clearly.',
      },
      {
        title: 'Audience & Channel Mapping',
        text: 'Matching institutional audiences with the channels that build authority.',
      },
      {
        title: 'Trust & Presence Building',
        text: 'Strengthening market presence so the brand earns confidence over time.',
      },
    ],
  },
  {
    slug: 'fintech-consulting',
    category: 'Fintech Consulting',
    title: 'Digital Transformation & Fintech',
    cardDescription:
      'Integrating advanced financial technologies and digital infrastructure to modernize operations and client experiences.',
    heroDescription:
      'VBS guides digital transformation across financial operations and client experience. We help you evaluate, integrate, and scale fintech infrastructure that improves efficiency, transparency, and long-term competitiveness.',
    cta: 'Learn More',
    icon: cpuIcon,
    viewTitle: 'A Holistic View of Your Digital Infrastructure.',
    viewDescription:
      'Our consultants assess platforms, data flows, and client experience to uncover where technology can improve efficiency and transparency. We work closely with your team to implement fintech solutions that scale with your operations.',
    viewItems: [
      'Technology stack assessment',
      'Process digitization planning',
      'Platform integration support',
      'Client experience optimization',
    ],
    deliverables: [
      {
        title: 'Technology Stack Assessment',
        text: 'Evaluating platforms and data flows to find where systems slow operations.',
      },
      {
        title: 'Process Digitization Planning',
        text: 'Prioritizing workflows that benefit most from automation and clarity.',
      },
      {
        title: 'Platform Integration Support',
        text: 'Connecting fintech tools so teams work from a single, reliable picture.',
      },
      {
        title: 'Client Experience Optimization',
        text: 'Improving digital journeys without compromising control or transparency.',
      },
    ],
  },
];

export const getSolutionBySlug = (slug) =>
  solutions.find((solution) => solution.slug === slug);
